import { Injectable, computed, signal } from '@angular/core';
import type { LayerDescriptor } from './feature-table.types';
import type { LayerFeaturePanelState, OpenLayerFeaturePanelParams } from './layer-feature-panel.types';

/**
 * Holds the ordered list of layers displayed in the floating feature-table panel, and the
 * index of the one currently shown — enables prev/next rotation between sibling layers
 * without closing the panel. Callers open/close the panel by setting the layer list.
 */
@Injectable({ providedIn: 'root' })
export class LayerFeaturePanelService {
  private readonly _state = signal<LayerFeaturePanelState | null>(null);
  readonly state = this._state.asReadonly();

  readonly activeLayer = computed<LayerDescriptor | null>(() => {
    const state = this._state();
    return state ? (state.layers[state.currentIndex] ?? null) : null;
  });

  open(params: OpenLayerFeaturePanelParams): void {
    const lastIndex = Math.max(params.layers.length - 1, 0);
    const startIndex = Math.min(Math.max(params.startIndex ?? 0, 0), lastIndex);
    this._state.set({ layers: params.layers, currentIndex: startIndex });
  }

  close(): void {
    this._state.set(null);
  }

  next(): void {
    this._state.update((state) =>
      state && state.currentIndex < state.layers.length - 1 ? { ...state, currentIndex: state.currentIndex + 1 } : state,
    );
  }

  previous(): void {
    this._state.update((state) => (state && state.currentIndex > 0 ? { ...state, currentIndex: state.currentIndex - 1 } : state));
  }
}
